import React, { useMemo, useState } from 'react';
import { Row, Col, Button, Select, Alert, Empty, Table, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ChartCard from '@/components/ChartCard';
import { useMonthlySettlement } from '@/api/monthlySettlement';
import type {
  MonthlySettlementRow,
  SettlementItem,
  MechanismProject,
} from '@/api/monthlySettlement';
import { format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';

const MonthlySettlementTab: React.FC = () => {
  const [selectedStation, setSelectedStation] = useState<string | undefined>(undefined);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const queryMonths = selectedMonths.length > 0 ? selectedMonths : undefined;

  const { data, isLoading, error, refetch } = useMonthlySettlement({
    station_name: selectedStation,
    months: queryMonths,
  });

  const rows: MonthlySettlementRow[] = data?.data?.rows ?? [];
  const availableMonths: string[] = data?.data?.available_months ?? [];
  const availableStations: string[] = data?.data?.available_stations ?? [];

  // ── Summary items table columns ──
  const summaryColumns = [
    { title: '类别', dataIndex: 'name', key: 'name', width: 220 },
    { title: '电量 (kWh)', dataIndex: 'energy', key: 'energy', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '电价 (元/kWh)', dataIndex: 'price', key: 'price', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '电费 (元)', dataIndex: 'fee', key: 'fee', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
  ];

  const mechColumns = [
    { title: '项目编码', dataIndex: 'project_code', key: 'project_code', width: 180 },
    { title: '实结机制电量', dataIndex: 'settled_energy', key: 'settled_energy', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '机制电价', dataIndex: 'mechanism_price', key: 'mechanism_price', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '结算参考价', dataIndex: 'reference_price', key: 'reference_price', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '月度电量比', dataIndex: 'energy_ratio', key: 'energy_ratio', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
    { title: '差价结算电费', dataIndex: 'diff_fee', key: 'diff_fee', align: 'right' as const,
      render: (v: unknown) => (typeof v === 'number' ? format2(v) : '-') },
  ];

  // ── KPI comparison chart (monthly total fee across stations) ──
  const chartOption: EChartsOption = useMemo(() => {
    if (!rows.length) return {};
    const months = [...new Set(rows.map((r) => r.settlement_month))].sort();
    const stations = [...new Set(rows.map((r) => r.station_name))];
    const colors = ['#0d9488', '#f59e0b', '#3b82f6', '#ef4444'];

    return {
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => typeof v === 'number' ? format2(v) : String(v) },
      legend: { bottom: 30 },
      grid: { left: 60, right: 20, top: 20, bottom: 80 },
      xAxis: { type: 'category', data: months },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `${(v / 10000).toFixed(0)}万` } },
      series: stations.map((s, i) => ({
        name: s,
        type: 'bar',
        data: months.map((m) => {
          const r = rows.find((x) => x.station_name === s && x.settlement_month === m);
          return r?.total_fee ?? null;
        }),
        itemStyle: { color: colors[i % colors.length] },
      })),
      dataZoom: [{ type: 'slider', start: 0, end: 100, bottom: 40 }],
    };
  }, [rows]);

  // ── Expandable row render ──
  const expandedRowRender = (record: MonthlySettlementRow) => (
    <Tabs
      items={[
        {
          key: 'summary',
          label: '费用明细',
          children: (
            <Table
              columns={summaryColumns}
              dataSource={(record.summary_items || []).map((it: SettlementItem, i: number) => ({ ...it, key: i }))}
              size="small"
              pagination={false}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><strong>合计</strong></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{format2(record.total_fee)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          ),
        },
        {
          key: 'mechanism',
          label: '机制电费',
          children: (record.mechanism_projects || []).length === 0 ? (
            <Empty description="无机制电费项目" />
          ) : (
            <Table
              columns={mechColumns}
              dataSource={(record.mechanism_projects || []).map((mp: MechanismProject, i: number) => ({ ...mp, key: i }))}
              size="small"
              pagination={false}
            />
          ),
        },
      ]}
      size="small"
    />
  );

  return (
    <div>
      {/* Filter bar */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站</div>
            <Select
              allowClear
              value={selectedStation}
              options={availableStations.map((s) => ({ value: s, label: s }))}
              onChange={(v) => setSelectedStation(v)}
              style={{ width: '100%' }}
              placeholder="全部场站"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>结算月份</div>
            <Select
              mode="multiple"
              value={selectedMonths}
              options={availableMonths.map((m) => ({ value: m, label: m }))}
              onChange={(v) => setSelectedMonths(v)}
              style={{ width: '100%' }}
              placeholder="全部月份"
            />
          </Col>
          <Col xs={24} sm={4} md={3}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => refetch()} style={{ width: '100%' }}>
              查询
            </Button>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert message={error instanceof Error ? error.message : '请求失败'} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Empty description="暂无月结算数据，请前往「场站数据上传 → 月结算上传」导入 PDF 结算单" style={{ marginTop: 48 }} />
      )}

      {/* Chart */}
      {rows.length > 0 && (
        <ChartCard title="各场站月结算电费对比" option={chartOption} loading={isLoading} error={null} height={350} />
      )}

      {/* Records list */}
      {rows.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 12 }}>月结算单</div>
          <Table
            columns={[
              { title: '结算月份', dataIndex: 'settlement_month', key: 'settlement_month', width: 100 },
              { title: '场站', dataIndex: 'station_name', key: 'station_name', width: 140 },
              { title: '上网电量 (kWh)', dataIndex: 'total_energy', key: 'total_energy', align: 'right' as const,
                render: (v: number) => format2(v) },
              { title: '上网均价 (元/kWh)', dataIndex: 'total_avg_price', key: 'total_avg_price', align: 'right' as const,
                render: (v: number) => format2(v) },
              { title: '结算金额 (元)', dataIndex: 'total_fee', key: 'total_fee', align: 'right' as const,
                render: (v: number) => <strong>{format2(v)}</strong> },
              { title: '费用项', key: 'item_count', align: 'center' as const, width: 80,
                render: (_: unknown, r: MonthlySettlementRow) => (r.summary_items || []).length },
              { title: '机制项目', key: 'mech_count', align: 'center' as const, width: 80,
                render: (_: unknown, r: MonthlySettlementRow) => (r.mechanism_projects || []).length },
            ]}
            dataSource={rows.map((r, i) => ({ ...r, key: i }))}
            loading={isLoading}
            size="small"
            expandable={{ expandedRowRender, rowExpandable: (r) => (r.summary_items || []).length > 0 }}
            pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
          />
        </div>
      )}

    </div>
  );
};

export default MonthlySettlementTab;
